import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import DashboardLayout from "../layouts/DashboardLayout";
import getApiUrl from "../utils/api";
import "./RolePermission.css";

const ROLE_TEMPLATES = [
  {
    value: "custom",
    label: "Custom configuration",
    scope: null,
    permissions: [],
    description: "Manually choose scope and individual permissions.",
  },
  {
    value: "super_admin",
    label: "Super Admin",
    scope: "global",
    permissions: ["view", "create", "edit", "delete"],
    description:
      "Full access across all departments and projects with unlimited assignment capability.",
  },
  {
    value: "department_manager",
    label: "Department Manager",
    scope: "department",
    permissions: ["view", "create", "edit", "delete"],
    description: "Full management capabilities for the selected department.",
  },
  {
    value: "manager_delegate",
    label: "Manager Delegate",
    scope: "user",
    permissions: ["view", "create", "edit", "delete"],
    description:
      "Delegate department task management to a trusted teammate. Automatically links back to you.",
  },
  {
    value: "department_viewer",
    label: "Department Viewer",
    scope: "department",
    permissions: ["view"],
    description: "Read-only visibility across the selected department.",
  },
  {
    value: "project_manager",
    label: "Project Manager",
    scope: "project",
    permissions: ["view", "create", "edit", "delete"],
    description: "Manage and update all work for the selected project.",
  },
  {
    value: "project_contributor",
    label: "Project Contributor",
    scope: "project",
    permissions: ["view", "edit"],
    description:
      "Update tasks within the chosen project while respecting limits.",
  },
];

const RolePermission = () => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [userFetchError, setUserFetchError] = useState("");
  const [assignerMeta, setAssignerMeta] = useState({
    isSuperAdmin: false,
    managedDepartments: [],
    managedProjects: [],
    canAssignDepartments: false,
    canAssignProjects: false,
  });
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [metaError, setMetaError] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [roleTemplate, setRoleTemplate] = useState("custom");
  const [roleType, setRoleType] = useState("global");
  const [targetId, setTargetId] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);

  const canAssignAnything =
    assignerMeta.isSuperAdmin ||
    assignerMeta.canAssignDepartments ||
    assignerMeta.canAssignProjects;

  const templateOptions = useMemo(() => {
    if (!assignerMeta.isSuperAdmin && !canAssignAnything) {
      return ROLE_TEMPLATES.filter((tpl) => tpl.value === "custom");
    }

    return ROLE_TEMPLATES.filter((template) => {
      if (template.value === "custom") {
        return canAssignAnything || assignerMeta.isSuperAdmin;
      }

      if (!assignerMeta.isSuperAdmin) {
        if (
          template.value === "super_admin" ||
          template.value === "department_manager"
        ) {
          return false;
        }
        if (template.value === "project_manager") {
          return assignerMeta.canAssignProjects;
        }
      }

      if (template.value === "manager_delegate") {
        return assignerMeta.isSuperAdmin || assignerMeta.canAssignDepartments;
      }

      if (!assignerMeta.isSuperAdmin) {
        if (
          template.scope === "department" &&
          !assignerMeta.canAssignDepartments
        ) {
          return false;
        }
        if (template.scope === "project" && !assignerMeta.canAssignProjects) {
          return false;
        }
      }

      return true;
    });
  }, [assignerMeta, canAssignAnything]);

  const roleTemplateOptions = useMemo(
    () =>
      templateOptions.map((template) => ({
        value: template.value,
        label: template.label,
        data: template,
      })),
    [templateOptions]
  );

  const selectedTemplateOption = useMemo(
    () =>
      roleTemplateOptions.find((option) => option.value === roleTemplate) ||
      null,
    [roleTemplateOptions, roleTemplate]
  );

  const manualScopeOptions = useMemo(() => {
    const options = [];

    if (assignerMeta.isSuperAdmin) {
      options.push({
        value: "global",
        label: "Global (all departments & projects)",
      });
    }

    if (assignerMeta.isSuperAdmin || assignerMeta.canAssignDepartments) {
      options.push({
        value: "department",
        label: "Department (choose target)",
      });
    }

    if (assignerMeta.isSuperAdmin || assignerMeta.canAssignProjects) {
      options.push({ value: "project", label: "Project (choose target)" });
    }

    return options;
  }, [assignerMeta]);

  const canSubmitAssignments = assignerMeta.isSuperAdmin || canAssignAnything;

  useEffect(() => {
    if (roleTemplate !== "custom") return;
    if (!manualScopeOptions.length) return;
    if (!manualScopeOptions.some((option) => option.value === roleType)) {
      setRoleType(manualScopeOptions[0].value);
    }
  }, [roleTemplate, manualScopeOptions, roleType]);

  const buildPermissionState = useCallback(
    (activePermissions = []) => {
      if (!permissionsList.length) return {};
      const activeSet = new Set(activePermissions);
      const base = {};
      permissionsList.forEach(({ name }) => {
        base[name] = activeSet.has(name);
      });
      return base;
    },
    [permissionsList]
  );

  const selectedTemplate =
    ROLE_TEMPLATES.find((tpl) => tpl.value === roleTemplate) ||
    ROLE_TEMPLATES[0];

  const applyTemplate = useCallback(
    (templateValue) => {
      const template = ROLE_TEMPLATES.find(
        (tpl) => tpl.value === templateValue
      );
      if (!template) return;

      setRoleTemplate(templateValue);

      if (!template.scope) {
        setRoleType("global");
        setTargetId("");
      } else {
        setRoleType(template.scope);
        if (template.scope === "user") {
          setTargetId(currentUserId ? String(currentUserId) : "");
        } else {
          setTargetId("");
        }
      }

      if (permissionsList.length) {
        setPermissions(buildPermissionState(template.permissions));
      }
    },
    [buildPermissionState, currentUserId, permissionsList]
  );
  const [showMyRoles, setShowMyRoles] = useState(false);
  const [myRoles, setMyRoles] = useState([]);
  const [myRolesLoading, setMyRolesLoading] = useState(false);
  const [myRolesError, setMyRolesError] = useState("");

  const userOptions = useMemo(() => {
    return users.map((u) => {
      const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return {
        value: String(u.id),
        label: fullName || u.email || "Unknown user",
        data: u,
      };
    });
  }, [users]);

  const selectedUserOption = useMemo(
    () =>
      userOptions.find((option) => option.value === String(selectedUser)) ||
      null,
    [userOptions, selectedUser]
  );

  const formatUserOption = useCallback((option) => {
    const email = option?.data?.email;
    return (
      <div className="role-perm-user-option">
        <span className="role-perm-user-name">{option.label}</span>
        {email && <span className="role-perm-user-email">{email}</span>}
      </div>
    );
  }, []);

  const noUserOptionsMessage = useCallback(() => "No eligible users found", []);

  const departmentOptions = useMemo(
    () =>
      departments.map((dept) => ({
        value: String(dept.id),
        label: dept.name || `Department ${dept.id}`,
      })),
    [departments]
  );

  const projectOptions = useMemo(() => {
    if (!Array.isArray(projects)) {
      return [];
    }
    return projects.map((project) => ({
      value: String(project.id),
      label: project.name || `Project ${project.id}`,
    }));
  }, [projects]);

  const selectedDepartmentOption = useMemo(() => {
    if (roleType !== "department") return null;
    return (
      departmentOptions.find((option) => option.value === String(targetId)) ||
      null
    );
  }, [roleType, departmentOptions, targetId]);

  const selectedProjectOption = useMemo(() => {
    if (roleType !== "project") return null;
    return (
      projectOptions.find((option) => option.value === String(targetId)) || null
    );
  }, [roleType, projectOptions, targetId]);

  // TODO: fetch users, departments, projects
  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const extractedId =
        storedUser?.id || storedUser?.userId || storedUser?.user_id;
      if (extractedId) {
        setCurrentUserId(extractedId);
      }
    } catch (err) {
      console.error("Error parsing stored user:", err);
    }

    // fetch users
    fetch(getApiUrl("api/users"), { credentials: "include" })
      .then((res) => {
        console.log("Users fetch response:", res.status, res.ok);
        if (!res.ok) {
          throw new Error(
            res.status === 403
              ? "You are not allowed to assign roles outside your hierarchy."
              : "Failed to load assignable users."
          );
        }
        return res.json();
      })
      .then((data) => {
        console.log("Users data:", data);
        const normalized = Array.isArray(data) ? data : [];
        setUsers(normalized);
        setUserFetchError(
          normalized.length
            ? ""
            : "No eligible users found in your reporting hierarchy."
        );
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
        setUsers([]);
        setUserFetchError(err.message || "Unable to load users.");
      });
    // fetch departments
    fetch(getApiUrl("api/departments"), { credentials: "include" })
      .then((res) => {
        console.log("Departments fetch response:", res.status, res.ok);
        return res.json();
      })
      .then((data) => {
        console.log("Departments data:", data);
        setDepartments(data);
      })
      .catch((err) => console.error("Error fetching departments:", err));
    // fetch projects
    fetch(getApiUrl("api/projects"), { credentials: "include" })
      .then((res) => {
        console.log("Projects fetch response:", res.status, res.ok);
        return res.json();
      })
      .then((data) => {
        console.log("Projects data:", data);
        setProjects(data);
      })
      .catch((err) => console.error("Error fetching projects:", err));
    // Load dynamic permissions
    fetch(getApiUrl("api/permissions"), { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setPermissionsList(data))
      .catch((err) => console.error("Error loading permissions:", err));
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;

    const loadCapabilities = async () => {
      try {
        const res = await fetch(getApiUrl(`api/roles/${currentUserId}`), {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to load capabilities (${res.status})`);
        }
        const payload = await res.json();
        if (!Array.isArray(payload)) {
          throw new Error("Unexpected capability payload");
        }

        const deptSet = new Set();
        const projSet = new Set();
        let isSuper = false;
        let hasDeptManager = false;
        let hasProjectManager = false;

        payload.forEach((role) => {
          const roleName = role?.name;
          if (!roleName) return;
          if (roleName === "super_admin") {
            isSuper = true;
          }
          if (roleName === "department_manager") {
            hasDeptManager = true;
            const deptId = role?.departmentRole?.id || role?.department_id;
            if (deptId) {
              deptSet.add(String(deptId));
            }
          }
          if (roleName === "project_manager") {
            hasProjectManager = true;
            const projectId = role?.projectRole?.id || role?.project_id;
            if (projectId) {
              projSet.add(String(projectId));
            }
          }
        });

        if (!cancelled) {
          setAssignerMeta({
            isSuperAdmin: isSuper,
            managedDepartments: Array.from(deptSet),
            managedProjects: Array.from(projSet),
            canAssignDepartments: isSuper || hasDeptManager,
            canAssignProjects: isSuper || hasProjectManager,
          });
          setMetaError("");
        }
      } catch (err) {
        console.error("Error loading assigner capabilities:", err);
        if (!cancelled) {
          setAssignerMeta({
            isSuperAdmin: false,
            managedDepartments: [],
            managedProjects: [],
            canAssignDepartments: false,
            canAssignProjects: false,
          });
          setMetaError(
            "Your current role does not allow managing assignments."
          );
        }
      } finally {
        if (!cancelled) {
          setMetaLoaded(true);
        }
      }
    };

    loadCapabilities();
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!templateOptions.length) {
      setRoleTemplate("custom");
      setRoleType("global");
      setTargetId("");
      return;
    }
    if (!templateOptions.some((tpl) => tpl.value === roleTemplate)) {
      applyTemplate(templateOptions[0].value);
    }
  }, [templateOptions, roleTemplate, applyTemplate]);

  useEffect(() => {
    if (!permissionsList.length) return;
    setPermissions((prev) => {
      const base = buildPermissionState();
      const next = { ...base };
      Object.keys(prev || {}).forEach((key) => {
        if (key in next) {
          next[key] = Boolean(prev[key]);
        }
      });
      return next;
    });
  }, [permissionsList, buildPermissionState]);

  useEffect(() => {
    if (!permissionsList.length || roleTemplate === "custom") return;
    setPermissions(buildPermissionState(selectedTemplate?.permissions));
  }, [permissionsList, roleTemplate, selectedTemplate, buildPermissionState]);

  const handlePermissionChange = (perm) => {
    setPermissions((prev) => ({
      ...prev,
      [perm]: !Boolean(prev?.[perm]),
    }));
    if (roleTemplate !== "custom") {
      setRoleTemplate("custom");
    }
  };

  useEffect(() => {
    if (roleType === "user") {
      setTargetId(currentUserId ? String(currentUserId) : "");
    } else if (roleType !== "department" && roleType !== "project") {
      setTargetId("");
    }
  }, [roleType, currentUserId]);

  const handleToggleMyRoles = async () => {
    if (showMyRoles) {
      setShowMyRoles(false);
      return;
    }

    setShowMyRoles(true);

    if (!currentUserId) {
      setMyRoles([]);
      setMyRolesError(
        "Unable to determine the signed-in user. Please sign in again."
      );
      return;
    }

    setMyRolesLoading(true);
    setMyRolesError("");

    try {
      const response = await fetch(getApiUrl(`api/roles/${currentUserId}`), {
        credentials: "include",
      });

      let payload;
      try {
        payload = await response.json();
      } catch (parseErr) {
        console.error("Failed to parse roles response:", parseErr);
        throw new Error("Unable to read roles from the server.");
      }

      if (!response.ok) {
        const message =
          (payload && payload.message) ||
          `Failed to load roles (status ${response.status})`;
        throw new Error(message);
      }

      setMyRoles(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error("Error loading user roles:", err);
      setMyRolesError(err.message || "Unable to load your roles right now.");
      setMyRoles([]);
    } finally {
      setMyRolesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert("Select a user to assign permissions.");
      return;
    }

    if (!assignerMeta.isSuperAdmin && !canAssignAnything) {
      alert("You are not authorized to assign roles.");
      return;
    }

    if ((roleType === "department" || roleType === "project") && !targetId) {
      const label = roleType === "department" ? "department" : "project";
      alert(`Select a ${label} for this scope.`);
      return;
    }

    const normalizedPermissions = permissionsList.reduce((acc, perm) => {
      acc[perm.name] = Boolean(permissions?.[perm.name]);
      return acc;
    }, {});

    const activePermissionCount = Object.values(normalizedPermissions).filter(
      Boolean
    ).length;
    if (!activePermissionCount) {
      alert("Enable at least one permission.");
      return;
    }

    const computedRoleName =
      roleTemplate === "custom" ? `custom_${roleType || "role"}` : roleTemplate;

    let scopeTarget = null;
    if (roleType === "department" || roleType === "project") {
      scopeTarget = targetId;
    } else if (roleType === "user") {
      scopeTarget = targetId || (currentUserId ? String(currentUserId) : "");
      if (!scopeTarget) {
        alert(
          "Delegated roles require a valid manager reference. Please refresh and try again."
        );
        return;
      }
    }

    const payload = {
      selectedUser,
      roleType,
      targetId: scopeTarget,
      permissions: normalizedPermissions,
      roleName: computedRoleName,
    };
    try {
      const res = await fetch(getApiUrl("api/roles"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        alert("Roles assigned successfully!");
        // Optionally reset form
        setSelectedUser("");
        setRoleTemplate("custom");
        setRoleType("global");
        setTargetId("");
        setPermissions(buildPermissionState());
      } else {
        alert(data.message || "Failed to assign roles");
      }
    } catch (err) {
      console.error(err);
      alert("Server error. Please try again later.");
    }
  };

  return (
    <DashboardLayout>
      <div className="role-perm-page">
        <section className="role-perm-hero">
          <div className="role-perm-hero-text">
            <h1>Assign Roles & Permissions</h1>
          </div>
          <div className="role-perm-hero-actions">
            <button
              type="button"
              className={`role-perm-secondary-btn ${
                showMyRoles ? "is-active" : ""
              }`}
              onClick={handleToggleMyRoles}
            >
              {showMyRoles ? "Hide My Roles" : "View My Roles & Permissions"}
            </button>
          </div>
        </section>

        <div className="role-perm-grid">
          <div className="role-perm-card role-perm-form-card">
            <div className="role-perm-card-header">
              <h2>Assign access</h2>
            </div>

            <form className="role-perm-form" onSubmit={handleSubmit}>
              {metaError && (
                <div className="role-perm-banner role-perm-banner--error">
                  {metaError}
                </div>
              )}
              {!metaError && metaLoaded && !canSubmitAssignments && (
                <div className="role-perm-banner role-perm-banner--muted">
                  You can review access but need elevated permissions to assign
                  new roles.
                </div>
              )}

              <div className="role-perm-form-group">
                <label>User</label>
                <Select
                  className="role-perm-select"
                  classNamePrefix="role-perm-select"
                  placeholder="Select user"
                  options={userOptions}
                  value={selectedUserOption}
                  onChange={(option) =>
                    setSelectedUser(option ? option.value : "")
                  }
                  isClearable
                  isDisabled={!userOptions.length}
                  formatOptionLabel={formatUserOption}
                  noOptionsMessage={noUserOptionsMessage}
                />
                {userFetchError && (
                  <p className="role-perm-helper">{userFetchError}</p>
                )}
              </div>
              <div className="role-perm-form-group">
                <label>Role template</label>
                <Select
                  className="role-perm-select"
                  classNamePrefix="role-perm-select"
                  placeholder="Select template"
                  options={roleTemplateOptions}
                  value={selectedTemplateOption}
                  onChange={(option) => {
                    if (option) {
                      applyTemplate(option.value);
                    }
                  }}
                  isDisabled={!roleTemplateOptions.length}
                  isClearable={false}
                />
                {!templateOptions.length && (
                  <p className="role-perm-helper role-perm-helper--warning">
                    You don&apos;t have permission to assign any of the
                    available templates.
                  </p>
                )}
                {selectedTemplate?.description && (
                  <p className="role-perm-helper">
                    {selectedTemplate.description}
                  </p>
                )}
                {selectedTemplate?.scope && (
                  <p className="role-perm-helper">
                    Scope automatically set to {selectedTemplate.scope}.
                  </p>
                )}
              </div>

              {roleTemplate === "custom" && (
                <div className="role-perm-form-group">
                  <label>Scope</label>
                  {manualScopeOptions.length ? (
                    <div className="role-perm-scope-options">
                      {manualScopeOptions.map((option) => (
                        <label
                          key={option.value}
                          className={`role-perm-scope-chip ${
                            roleType === option.value ? "is-active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="role-scope"
                            value={option.value}
                            checked={roleType === option.value}
                            onChange={() => setRoleType(option.value)}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="role-perm-helper role-perm-helper--warning">
                      Your current role can&apos;t assign departmental or
                      project-scoped access.
                    </p>
                  )}
                </div>
              )}

              {roleType === "department" && (
                <div className="role-perm-form-group">
                  <label>Department</label>
                  <Select
                    className="role-perm-select"
                    classNamePrefix="role-perm-select"
                    placeholder="Select department"
                    options={departmentOptions}
                    value={selectedDepartmentOption}
                    onChange={(option) =>
                      setTargetId(option ? option.value : "")
                    }
                    isClearable
                    isDisabled={!departmentOptions.length}
                  />
                </div>
              )}

              {roleType === "project" && (
                <div className="role-perm-form-group">
                  <label>Project</label>
                  <Select
                    className="role-perm-select"
                    classNamePrefix="role-perm-select"
                    placeholder="Select project"
                    options={projectOptions}
                    value={selectedProjectOption}
                    onChange={(option) =>
                      setTargetId(option ? option.value : "")
                    }
                    isClearable
                    isDisabled={!projectOptions.length}
                  />
                </div>
              )}

              <div className="permissions-group">
                {permissionsList.length === 0 ? (
                  <p className="permissions-empty">
                    No permission definitions available.
                  </p>
                ) : (
                  permissionsList.map((perm) => (
                    <label key={perm.id || perm.name}>
                      <input
                        type="checkbox"
                        checked={Boolean(permissions?.[perm.name])}
                        onChange={() => handlePermissionChange(perm.name)}
                      />
                      {perm.name.charAt(0).toUpperCase() + perm.name.slice(1)}
                    </label>
                  ))
                )}
              </div>

              <button
                className="role-perm-submit-btn"
                type="submit"
                disabled={!canSubmitAssignments}
              >
                Save assignment
              </button>
            </form>
          </div>

          <aside
            className={`role-perm-card my-roles-card ${
              showMyRoles ? "is-open" : ""
            }`}
          >
            <div className="my-roles-card-header">
              <h2>Your current access</h2>
            </div>

            <div className="my-roles-panel">
              {showMyRoles ? (
                <>
                  {myRolesLoading && (
                    <p className="my-roles-status">Loading your roles…</p>
                  )}
                  {!myRolesLoading && myRolesError && (
                    <p className="my-roles-status my-roles-error">
                      {myRolesError}
                    </p>
                  )}
                  {!myRolesLoading && !myRolesError && myRoles.length === 0 && (
                    <p className="my-roles-status">
                      No roles assigned to you yet.
                    </p>
                  )}
                  {!myRolesLoading && !myRolesError && myRoles.length > 0 && (
                    <ul className="my-roles-list">
                      {myRoles.map((role, index) => {
                        const scopeLabel = role?.departmentRole
                          ? `Department • ${role.departmentRole.name}`
                          : role?.projectRole
                          ? `Project • ${role.projectRole.name}`
                          : "Global";
                        const permissionName =
                          role?.permission?.name || "Unknown permission";
                        const formattedPermission =
                          permissionName.charAt(0).toUpperCase() +
                          permissionName.slice(1);
                        const key =
                          role?.id ||
                          `${permissionName}-${scopeLabel}-${index}`;
                        const roleLabel = role?.name
                          ? role.name.replace(/_/g, " ")
                          : null;
                        return (
                          <li key={key} className="my-roles-item">
                            <div className="my-roles-item-info">
                              <span className="my-roles-permission">
                                {formattedPermission}
                              </span>
                              {roleLabel && (
                                <span className="my-roles-role">
                                  {roleLabel}
                                </span>
                              )}
                            </div>
                            <span className="my-roles-scope">{scopeLabel}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              ) : (
                <div className="my-roles-placeholder">
                  <span className="placeholder-badge">Personal view</span>
                  <h3>Review your access</h3>
                  <p>
                    Tap the button above to load a live snapshot of every
                    permission currently tied to your user.
                  </p>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RolePermission;
